import React, { useState, useEffect } from 'react';
import { API_BASE_URL, getApiHeaders } from '../config/api';
import './SemanticTrainer.css';

const SemanticTrainer = () => {
  const [conversations, setConversations] = useState([]);
  const [categories, setCategories] = useState([
    'FAMIGLIA', 'ALIMENTAZIONE', 'ORIENTAMENTO_TEMPORALE',
    'ORIENTAMENTO_SPAZIALE', 'RICHIESTA_AFFETTO', 'ANIMALI',
    'CASA', 'SALUTE'
  ]);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedRating, setSelectedRating] = useState('ALL');
  const [loading, setLoading] = useState(false);

  // Dialog "Insegna meglio" state
  const [teachDialogOpen, setTeachDialogOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [improvedResponse, setImprovedResponse] = useState('');
  const [isImprovingWithAI, setIsImprovingWithAI] = useState(false);

  // Banner suggerimento per rating basso
  const [showLowRatingBanner, setShowLowRatingBanner] = useState(null);

  useEffect(() => {
    loadConversations();
  }, [selectedCategory, selectedRating]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const url = selectedCategory === 'ALL'
        ? `${API_BASE_URL}/api/semantic/conversations/1?limit=50`
        : `${API_BASE_URL}/api/semantic/conversations/1?limit=50&category=${selectedCategory}`;

      console.log('🔄 Loading conversations from:', url);
      const response = await fetch(url, { headers: getApiHeaders() });
      const data = await response.json();
      console.log('📦 Conversations loaded:', data);

      // Filtra per rating se selezionato
      let filteredConversations = data.conversations || [];
      if (selectedRating !== 'ALL') {
        const ratingValue = parseInt(selectedRating);
        filteredConversations = filteredConversations.filter(
          conv => (conv.response_priority || 3) === ratingValue
        );
      }

      setConversations(filteredConversations);
    } catch (error) {
      console.error('❌ Error loading conversations:', error);
    }
    setLoading(false);
  };

  const handleSaveImprovedResponse = async () => {
    if (!improvedResponse.trim()) {
      alert('⚠️ Inserisci una risposta migliorata');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/training/teach`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          conversationId: selectedConversation.id,
          improvedResponse: improvedResponse
        })
      });

      if (response.ok) {
        alert('✅ Risposta migliorata salvata con successo!');
        setTeachDialogOpen(false);
        setSelectedConversation(null);
        setImprovedResponse('');
        loadConversations();
      } else {
        alert('❌ Errore nel salvataggio');
      }
    } catch (error) {
      console.error('Error teaching better:', error);
      alert('❌ Errore nel salvataggio');
    }
  };


  // Copia risposta negli appunti
  const handleCopyResponse = () => {
    if (selectedConversation) {
      navigator.clipboard.writeText(selectedConversation.ai_response);
      const btn = document.querySelector('.copy-btn');
      if (btn) {
        const originalText = btn.textContent;
        btn.textContent = '✓ Copiato';
        setTimeout(() => {
          btn.textContent = originalText;
        }, 2000);
      }
    }
  };

  // Migliora risposta con AI
  const handleImproveWithAI = async () => {
    if (!selectedConversation) return;
    
    setIsImprovingWithAI(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/training/improve-response`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          question: selectedConversation.user_input,
          originalResponse: selectedConversation.ai_response,
          context: `Categoria: ${selectedConversation.category}, Emozione: ${selectedConversation.emotion_detected}`
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setImprovedResponse(data.improvedResponse);
        console.log('✅ Risposta migliorata ricevuta');
      } else {
        console.error('❌ Errore:', data.error);
        alert('Errore nel miglioramento: ' + data.error);
      }
    } catch (error) {
      console.error('❌ Errore chiamata API:', error);
      alert('Errore di connessione');
    } finally {
      setIsImprovingWithAI(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Aggiorna priorità (stelle) di una conversazione
  const handleStarClick = async (conversationId, stars) => {
    try {
      console.log(`⭐ Click stella ${stars} per conversazione ${conversationId}`);

      // 1. Salva rating nel database
      const response = await fetch(`${API_BASE_URL}/api/semantic/conversations/${conversationId}/priority`, {
        method: 'PATCH',
        headers: getApiHeaders(),
        body: JSON.stringify({ priority: stars })
      });

      if (!response.ok) {
        throw new Error('Errore salvataggio rating');
      }

      console.log('✅ Rating salvato');

      // 2. Aggiorna UI locale (ottimistico)
      setConversations(prev => prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, response_priority: stars }
          : conv
      ));

      // 3. Se rating ≤2 → Mostra banner suggerimento
      if (stars <= 2) {
        setShowLowRatingBanner(conversationId);
        console.log('⚠️ Rating basso - mostro banner suggerimento');
      } else {
        setShowLowRatingBanner(null);
        console.log('✅ Rating buono - nessun suggerimento');
      }

    } catch (error) {
      console.error('❌ Errore handleStarClick:', error);
      alert('Errore durante il salvataggio del rating');
    }
  };

  // Funzione per aprire dialog da banner o da pulsante
  const handleOpenTeachDialog = (conv) => {
    setSelectedConversation(conv);
    setImprovedResponse('');
    setShowLowRatingBanner(null); // Chiudi banner
    setTeachDialogOpen(true); // Apri dialog
  };

  // Elimina una conversazione
  const handleDeleteConversation = async (conversationId) => {
    // Chiedi conferma
    if (!window.confirm('Sei sicuro di voler eliminare questa conversazione?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/semantic/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: getApiHeaders()
      });

      const data = await response.json();

      if (response.ok) {
        alert('✅ Conversazione eliminata con successo');
        loadConversations(); // Ricarica la lista
      } else {
        alert('❌ Errore: ' + (data.error || 'Eliminazione fallita'));
      }
    } catch (error) {
      console.error('❌ Errore eliminazione conversazione:', error);
      alert('❌ Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="semantic-trainer">
      <div className="page-header yellow">
        <h1>Semantic Trainer</h1>
        <p className="subtitle">Addestra l'AI con risposte del caregiver</p>
      </div>

      <div className="filters-section">
        <label>Filtra per categoria:</label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="ALL">Tutte le categorie</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        <label>Filtra per rating:</label>
        <select
          value={selectedRating}
          onChange={(e) => setSelectedRating(e.target.value)}
        >
          <option value="ALL">Tutti i rating</option>
          <option value="5">⭐⭐⭐⭐⭐ (5 stelle)</option>
          <option value="4">⭐⭐⭐⭐ (4 stelle)</option>
          <option value="3">⭐⭐⭐ (3 stelle)</option>
          <option value="2">⭐⭐ (2 stelle)</option>
          <option value="1">⭐ (1 stella)</option>
        </select>

        <button className="btn-refresh" onClick={loadConversations}>
          Aggiorna
        </button>
      </div>

      <div className="conversations-list">
        {loading ? (
          <div className="loading">Caricamento...</div>
        ) : conversations.length === 0 ? (
          <div className="empty-state">
            <p>Nessuna conversazione trovata</p>
          </div>
        ) : (
          conversations.map((conv) => (
            <div key={conv.id} className="conversation-card">
              <div className="conv-header">
                <span className="conv-category">{conv.category || 'N/A'}</span>
                <div className="conv-meta">
                  <div>{formatDate(conv.created_at)}</div>
                  <div className="conv-cost">${conv.cost?.toFixed(6) || '0.000000'}</div>
                </div>
              </div>

              <div className="conv-content">
                <div className="conv-row">
                  <span className="conv-label">Domanda:</span>
                  <span className="conv-text">{conv.user_input || 'N/A'}</span>
                </div>
                <div className="conv-row">
                  <span className="conv-label">Sem.AI:</span>
                  <span className="conv-text">{conv.ai_response}</span>
                </div>
              </div>

              <div className="conv-footer">
                <div className="conv-rating">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      key={num}
                      type="button"
                      className="star-btn"
                      onClick={() => handleStarClick(conv.id, num)}
                      title={`Vota ${num} stelle`}
                    >
                      {(conv.response_priority || 3) >= num ? '⭐' : '☆'}
                    </button>
                  ))}
                </div>
                <button
                  className="btn-train"
                  onClick={() => handleOpenTeachDialog(conv)}
                >
                  Addestra
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteConversation(conv.id)}
                  title="Elimina conversazione"
                >
                  🗑️ Elimina
                </button>
              </div>

              {/* Banner suggerimento per rating basso */}
              {showLowRatingBanner === conv.id && (
                <div className="low-rating-banner">
                  <div className="banner-content">
                    <span className="banner-icon">⚠️</span>
                    <span className="banner-text">
                      Rating basso. Vuoi insegnare una risposta migliore?
                    </span>
                  </div>
                  <div className="banner-actions">
                    <button
                      className="banner-btn cancel"
                      onClick={() => setShowLowRatingBanner(null)}
                      type="button"
                    >
                      No, grazie
                    </button>
                    <button
                      className="banner-btn primary"
                      onClick={() => handleOpenTeachDialog(conv)}
                      type="button"
                    >
                      Sì, insegna
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

{/* Dialog "Insegna meglio" */}
      {teachDialogOpen && (
        <div className="dialog-overlay" onClick={() => setTeachDialogOpen(false)}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="dialog-header">
              <h3>Insegna una Risposta Migliore</h3>
              <button
                className="dialog-close"
                onClick={() => setTeachDialogOpen(false)}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="dialog-body">

              {/* Domanda */}
              <div className="dialog-section">
                <label className="dialog-label">Domanda</label>
                <div className="dialog-text-box">
                  {selectedConversation?.user_input}
                </div>
              </div>

              {/* Risposta AI */}
              <div className="dialog-section">
                <div className="label-with-button">
                  <label className="dialog-label">Sem.AI attuale</label>
                  <button
                    className="btn-minimal copy-btn"
                    onClick={handleCopyResponse}
                    type="button"
                  >
                    Copia Risposta
                  </button>
                </div>
                <div className="dialog-text-box warning">
                  {selectedConversation?.ai_response}
                </div>
              </div>

              {/* Textarea */}
              <div className="dialog-section">
                <div className="label-with-button">
                  <label className="dialog-label">La tua risposta migliorata</label>
                  <button
                    className="btn-minimal btn-ai"
                    onClick={handleImproveWithAI}
                    disabled={isImprovingWithAI}
                    type="button"
                  >
                    {isImprovingWithAI ? 'Elaborazione...' : 'Migliora con AI'}
                  </button>
                </div>

                <textarea
                  className="dialog-textarea"
                  value={improvedResponse}
                  onChange={(e) => setImprovedResponse(e.target.value)}
                  placeholder="Scrivi qui la risposta corretta..."
                  rows={7}
                  autoFocus
                />

                <p className="dialog-hint">
                  Puoi copiare la risposta attuale o usare l'AI per migliorarla automaticamente.
                </p>
              </div>

            </div>

            {/* Footer */}
            <div className="dialog-footer">
              <button
                className="btn-secondary"
                onClick={() => setTeachDialogOpen(false)}
                type="button"
              >
                Annulla
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveImprovedResponse}
                disabled={!improvedResponse.trim()}
                type="button"
              >
                Salva e Insegna
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default SemanticTrainer;
